/**
 * Phase 2.1R+3 — /generate is now a redirect-only route.
 *
 * The legacy Generator wizard has been physically deleted. Any hit on
 * `/generate` (deep-link, bookmark, external referral) instantly
 * bootstraps a blank Pine v6 stub via `POST /api/scripts/blank` (no
 * LLM call, no credit debit) and lands the user directly inside the
 * Beam-AI Workspace at `/workspace/:id`.
 *
 * On failure we fall back to `/dashboard` and surface a toast — no
 * dead-end views, no white screens.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function NewWorkspaceRedirect() {
  const nav = useNavigate();
  const { t } = useTranslation();
  // React.StrictMode invokes effects mount → cleanup → mount in dev,
  // which used to combine badly with a `cancelled` flag captured in
  // the cleanup closure — cleanup would set cancelled=true before the
  // POST resolved, and the resolution then dropped the nav() call on
  // the floor. The `firedRef` guard is enough on its own: it prevents
  // duplicate POSTs, and the resolved nav() always runs. The route
  // instantly unmounts on nav(...) so no `setState-after-unmount`
  // risk exists here.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    (async () => {
      try {
        const { data } = await api.post("/scripts/blank");
        nav(`/workspace/${data.id}`, { replace: true });
      } catch (e) {
        toast.error(t("errors.generic", { defaultValue: "Something went wrong." }));
        nav("/dashboard", { replace: true });
      }
    })();
  }, [nav, t]);

  return (
    <div
      data-testid="new-workspace-redirect"
      className="min-h-[60vh] flex items-center justify-center text-zinc-400"
    >
      <div className="flex items-center gap-3 text-sm font-mono">
        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
        <span>{t("dashboard.new_generation", { defaultValue: "Opening workspace…" })}</span>
      </div>
    </div>
  );
}
